import React, { useEffect, useMemo, useState } from 'react';
import ordersService from '../../../services/ordersService';

const INITIAL_ORDER_CREATION_WINDOW_MS = 30 * 1000;

const FIELD_CONFIG = {
  order_products_qty: { label: 'Quantity', type: 'quantity' },
  quantity: { label: 'Quantity', type: 'quantity' },
  qty: { label: 'Quantity', type: 'quantity' },
  order_products_price: { label: 'Price', type: 'price' },
  unit_price: { label: 'Price', type: 'price' },
  price: { label: 'Price', type: 'price' },
  order_property_id: { label: 'Property', type: 'identifier' },
  note: { label: 'Note', type: 'text' },
  order_products_name: { label: 'Item name', type: 'text' },
  product_name: { label: 'Item name', type: 'text' },
};

const FIELD_PRIORITY = [
  'order_products_name',
  'product_name',
  'order_products_qty',
  'quantity',
  'qty',
  'order_products_price',
  'unit_price',
  'price',
  'note',
  'order_property_id',
];

const IGNORED_FIELDS = new Set([
  'id',
  'orders_id',
  'order_products_id',
  'created_at',
  'updated_at',
]);

function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function parseTimestamp(value) {
  if (!value) return null;
  const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value;
  const timestamp = new Date(normalized).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function eventAction(description = '') {
  if (/\b(created|added)\b/i.test(description)) return 'created';
  if (/\b(deleted|removed)\b/i.test(description)) return 'deleted';
  if (/\b(updated|changed|edited)\b/i.test(description)) return 'updated';
  return 'other';
}

function humanizeField(field) {
  const normalized = String(field)
    .replace(/^order_products_/, '')
    .replace(/^order_/, '')
    .replace(/_/g, ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function fieldDefinition(field) {
  return FIELD_CONFIG[field] || { label: humanizeField(field), type: 'text' };
}

function isEmptyValue(value) {
  return value === null || value === undefined || value === '';
}

function isNumericValue(value) {
  return !isEmptyValue(value) && Number.isFinite(Number(value));
}

function valuesEqual(field, before, after) {
  if (isEmptyValue(before) && isEmptyValue(after)) return true;
  const { type } = fieldDefinition(field);
  if ((type === 'quantity' || type === 'price' || type === 'identifier')
    && isNumericValue(before)
    && isNumericValue(after)) {
    return Number(before) === Number(after);
  }
  return String(before ?? '').trim() === String(after ?? '').trim();
}

function formatNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return Number.isInteger(number)
    ? String(number)
    : number.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function formatValue(field, value) {
  if (isEmptyValue(value)) return 'Not set';
  const { type } = fieldDefinition(field);
  if (type === 'price' && isNumericValue(value)) return `\u00a3${Number(value).toFixed(2)}`;
  if (type === 'quantity' && isNumericValue(value)) return formatNumber(value);
  if (type === 'identifier' && isNumericValue(value)) return `#${formatNumber(value)}`;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatSignedDelta(field, before, after) {
  const { type } = fieldDefinition(field);
  if ((type !== 'quantity' && type !== 'price')
    || !isNumericValue(before)
    || !isNumericValue(after)) return null;

  const delta = Number(after) - Number(before);
  if (delta === 0) return null;
  const sign = delta > 0 ? '+' : '-';
  const absolute = Math.abs(delta);
  return type === 'price'
    ? `${sign}\u00a3${absolute.toFixed(2)}`
    : `${sign}${formatNumber(absolute)}`;
}

function orderedVisibleFields(before, after) {
  const allFields = Array.from(new Set([
    ...Object.keys(before),
    ...Object.keys(after),
  ])).filter((field) => !IGNORED_FIELDS.has(field));

  return [
    ...FIELD_PRIORITY.filter((field) => allFields.includes(field)),
    ...allFields.filter((field) => !FIELD_PRIORITY.includes(field)).sort(),
  ];
}

function formatChanges(before, after) {
  return orderedVisibleFields(before, after)
    .filter((field) => !valuesEqual(field, before[field], after[field]))
    .map((field) => {
      const { label } = fieldDefinition(field);
      const delta = formatSignedDelta(field, before[field], after[field]);
      return `${label} changed: ${formatValue(field, before[field])} \u2192 ${formatValue(field, after[field])}${delta ? ` (${delta})` : ''}`;
    });
}

function formatSnapshot(values) {
  return orderedVisibleFields({}, values)
    .filter((field) => !isEmptyValue(values[field]))
    .map((field) => `${fieldDefinition(field).label}: ${formatValue(field, values[field])}`);
}

function itemLabel(log, before, after) {
  const name = after.order_products_name
    || after.product_name
    || before.order_products_name
    || before.product_name;
  if (name) return `Order item \u201c${name}\u201d`;
  return log.subject_id ? `Order item #${log.subject_id}` : 'Order item';
}

function isInitialItemCreation(log, orderCreatedAt) {
  if (log.is_order_edit) return false;
  if (eventAction(log.description) !== 'created') return false;
  const orderTimestamp = parseTimestamp(orderCreatedAt);
  const activityTimestamp = parseTimestamp(log.created_at);
  if (orderTimestamp === null || activityTimestamp === null) return false;
  return Math.abs(activityTimestamp - orderTimestamp) <= INITIAL_ORDER_CREATION_WINDOW_MS;
}

function formatActivity(log, orderCreatedAt) {
  const before = asRecord(log.old_values);
  const after = asRecord(log.new_values);
  const action = eventAction(log.description);

  if (isInitialItemCreation(log, orderCreatedAt)) return null;

  const label = itemLabel(log, before, after);
  let title;
  let changes;

  if (action === 'created') {
    changes = formatSnapshot(after);
    // Empty item-created records are repetitive activity noise. A later
    // creation with values remains visible as a real added-item change.
    if (changes.length === 0) return null;
    title = `${label} added`;
  } else if (action === 'deleted') {
    changes = formatSnapshot(Object.keys(before).length > 0 ? before : after);
    title = `${label} removed`;
  } else {
    changes = formatChanges(before, after);
    if (changes.length === 0) {
      if (action === 'updated') return null;
      title = log.description || `${label} changed`;
    } else {
      title = `${label} updated`;
    }
  }

  return {
    key: `activity-${log.id}`,
    title,
    timestamp: log.created_at,
    changes,
    user: log.user,
    impersonatedBy: log.impersonated_by,
    sourceIndex: 0,
  };
}

export function buildOrderHistoryEntries(logs, orderCreatedAt) {
  const activities = (Array.isArray(logs) ? logs : [])
    .map((log, sourceIndex) => {
      const activity = formatActivity(log, orderCreatedAt);
      return activity ? { ...activity, sourceIndex } : null;
    })
    .filter(Boolean);

  if (orderCreatedAt) {
    activities.push({
      key: 'order-placed',
      title: 'Order placed',
      timestamp: orderCreatedAt,
      changes: [],
      user: null,
      impersonatedBy: null,
      sourceIndex: activities.length,
      isOrderPlaced: true,
    });
  }

  return activities.sort((left, right) => {
    const leftTime = parseTimestamp(left.timestamp);
    const rightTime = parseTimestamp(right.timestamp);
    if (leftTime !== null && rightTime !== null && leftTime !== rightTime) {
      return rightTime - leftTime;
    }
    return left.sourceIndex - right.sourceIndex;
  });
}

const OrderLogsPanel = ({ orderId }) => {
  const [logs, setLogs] = useState([]);
  const [createdAt, setCreatedAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    setLogs([]);
    setCreatedAt('');

    ordersService.fetchOrderLogsById(orderId)
      .then((data) => {
        if (!active) return;
        setLogs(data?.logs ?? []);
        setCreatedAt(data?.order_created_at ?? '');
      })
      .catch(() => {
        if (active) setLoadError('Could not load order history.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [orderId]);

  const entries = useMemo(
    () => buildOrderHistoryEntries(logs, createdAt),
    [logs, createdAt],
  );

  return (
    <div style={{ border: '1px solid #ccc', marginTop: 20, padding: 12, borderRadius: 6 }}>
      <h3>Order History &amp; Activity</h3>
      <hr />

      {loading ? (
        <p>Loading order history...</p>
      ) : loadError ? (
        <p role="alert">{loadError}</p>
      ) : entries.length === 0 ? (
        <p>No meaningful activity found.</p>
      ) : (
        entries.map((entry) => {
          const impersonatorName = typeof entry.impersonatedBy === 'string'
            ? entry.impersonatedBy
            : entry.impersonatedBy?.name;
          const userName = entry.user?.name ?? 'System';

          return (
            <div key={entry.key} style={{ marginBottom: 14 }}>
              <strong>{entry.title}</strong>
              {entry.timestamp ? <> — {entry.timestamp}</> : null}
              {entry.changes.length > 0 && (
                <ul style={{ marginTop: 6, marginBottom: 6, paddingLeft: 22 }}>
                  {entry.changes.map((change, index) => (
                    <li key={`${entry.key}-change-${index}`}>{change}</li>
                  ))}
                </ul>
              )}
              {!entry.isOrderPlaced && (
                <>
                  <br />
                  <small>User: {userName}</small>
                </>
              )}
              {impersonatorName && (
                <div style={{ color: '#888' }}>
                  (Logged in as {userName}, real user: {impersonatorName})
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default OrderLogsPanel;
